import { useCallback } from 'react';

import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import IsMarkdownModeQuery from './__generated__/IsMarkdownModeQuery.graphql.ts';
import type {
	IsMarkdownModeQuery$data,
	IsMarkdownModeQuery$variables,
} from './__generated__/IsMarkdownModeQuery.graphql.ts';
import {
	default as UpdateMarkdownModeMutation,
	type UpdateMarkdownModeMutation$data,
	type UpdateMarkdownModeMutation$variables,
} from './__generated__/UpdateMarkdownModeMutation.graphql.ts';

export type UseUpdateMarkdownModeResult = {
	updateMarkdownMode: (
		contentId: string,
		isMarkdownMode: boolean,
	) => Promise<{
		success: boolean;
		errorMessage?: string;
	}>;
	loading: boolean;
	error: Error | undefined | null;
};

/**
 * Toggles a live page between markdown and rich-text modes. Returns a
 * normalised `{ success, errorMessage }` so callers don't have to dig through
 * the mutation payload.
 */
export const useUpdateMarkdownMode = (): UseUpdateMarkdownModeResult => {
	const { cloudId } = useSessionData();

	const [updateMarkdownModeMutation, { loading, error }] = useMutationInterop<
		UpdateMarkdownModeMutation$data,
		UpdateMarkdownModeMutation$variables
	>(UpdateMarkdownModeMutation, {
		// Payload has no normalisable entity, so manually patch the cached
		// `IsMarkdownModeQuery` for the affected contentId.
		update: (_, mutationResult) => {
			const payload = mutationResult.data?.confluence_updateMarkdownMode;
			if (!payload?.success || payload.isMarkdownMode == null) {
				return;
			}

			try {
				const cached = readQueryInterop<IsMarkdownModeQuery$data, IsMarkdownModeQuery$variables>({
					query: IsMarkdownModeQuery,
					variables: {
						contentId: payload.contentId,
						isMarkdownModeEnabled: expValEquals('cc-markdown-mode', 'isEnabled', true),
					},
				});

				const existingNodes = cached?.content?.nodes ?? [];
				const updatedNodes = existingNodes.map((node) => {
					if (!node || node.id !== payload.contentId) {
						return node;
					}
					return {
						...node,
						contentProperties: {
							...node.contentProperties,
							latest: {
								...node.contentProperties?.latest,
								isMarkdownMode: payload.isMarkdownMode,
							},
						},
					};
				});

				writeQueryInterop<IsMarkdownModeQuery$data, IsMarkdownModeQuery$variables>({
					query: IsMarkdownModeQuery,
					variables: {
						contentId: payload.contentId,
						isMarkdownModeEnabled: expValEquals('cc-markdown-mode', 'isEnabled', true),
					},
					data: {
						content: {
							...cached?.content,
							nodes: updatedNodes,
						},
					},
				});
			} catch {
				// Cache miss (query never ran for this contentId) — nothing to patch.
			}
		},
	});

	const updateMarkdownMode = useCallback(
		async (contentId: string, isMarkdownMode: boolean) => {
			try {
				const result = await updateMarkdownModeMutation({
					variables: {
						cloudId,
						input: {
							contentId,
							isMarkdownMode,
						},
					},
				});

				const payload = result?.data?.confluence_updateMarkdownMode;
				if (payload?.success) {
					return { success: true };
				}

				const errorMessage = payload?.errors?.[0]?.message ?? undefined;
				return { success: false, errorMessage };
			} catch (e) {
				const errorMessage = e instanceof Error ? e.message : undefined;
				return { success: false as const, errorMessage };
			}
		},
		[cloudId, updateMarkdownModeMutation],
	);

	return { updateMarkdownMode, loading, error };
};
